import { useCallback, useMemo } from 'react';
import { Platform } from 'react-native';

import { useResponsive } from '@sg/garnish';

import { useHeaderButtonOptions } from '../../../../../components/Header';
import { DefaultHeaderLeft } from '../../../../components/DefaultHeaderLeft';
import {
  DEFAULT_SCREEN_OPTIONS,
  SCREEN_HEADER_OPTIONS,
  type ScreenOptionsMap,
} from '../../../../constants';
import { useRouteTitles } from '../../../messages';

// ─────────────────────────────────────────────────────────────────────────────

export const useAccountTabStackOptions = () => {
  const { currentBreakpoint } = useResponsive();
  const titles = useRouteTitles();
  const headerLeft = useCallback(() => <DefaultHeaderLeft fallbackHref="/account/menu" />, []);
  const { headerLeftOptions } = useHeaderButtonOptions({ headerLeft });

  // ─────────────────────────────────────────────────────────────────────

  const stackOptions = useMemo(
    () => ({
      ...DEFAULT_SCREEN_OPTIONS,
      ...SCREEN_HEADER_OPTIONS,
      ...headerLeftOptions,
      headerShown: currentBreakpoint.isXS,
      headerTitle: undefined,
    }),
    [currentBreakpoint.isXS, headerLeftOptions],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(() => {
    return {
      //
      // ─── Main Stack Options (applied to all screens) ─────────────────────

      Stack: stackOptions,

      // ─── Individual Tabs ─────────────────────────────────────────────────

      AccountMenu: {
        customDocumentTitle: titles.accountMenuCustomDocumentTitle,
      },
      Profile: {
        title: titles.profileTitle,
        customDocumentTitle: titles.profileCustomDocumentTitle,
      },
      Payments: {
        title: titles.paymentsTitle,
        customDocumentTitle: titles.paymentsCustomDocumentTitle,
        headerShown: Platform.OS === 'web' && currentBreakpoint.isXS,
      },
      CreditAndPromoCodes: {
        title: titles.creditAndPromoCodesTitle,
        customDocumentTitle: titles.creditAndPromoCodesCustomDocumentTitle,
      },
      GiftCards: {
        title: titles.giftCardsTitle,
        customDocumentTitle: titles.accountGiftCardsCustomDocumentTitle,
      },
      GiftCardConfirmation: {
        title: titles.giftCardConfirmationTitle,
        customDocumentTitle: titles.giftCardConfirmationCustomDocumentTitle,
      },
      Gift: {
        title: titles.giftCardsTabTitle,
        customDocumentTitle: titles.giftCardsTabCustomDocumentTitle,
        headerShown: false,
        navbarLayout: 'static',
      },
      GiftCheckout: {
        title: titles.giftCardCheckoutTitle,
        customDocumentTitle: titles.giftCardCheckoutCustomDocumentTitle,
        headerTitle: '',
        navbarLayout: 'static-with-border',
      },
      GiftConfirmation: {
        title: titles.giftCardConfirmationTitle,
        customDocumentTitle: titles.giftCardConfirmationCustomDocumentTitle,
        headerTitle: '',
      },
      GiftRedeem: {
        title: titles.redeemGiftCardTitle,
        customDocumentTitle: titles.giftCardRedemptionCustomDocumentTitle,
        headerShown: false,
        navbarLayout: 'static-with-border',
      },
      Addresses: {
        title: titles.addressesTitle,
        customDocumentTitle: titles.addressesCustomDocumentTitle,
      },
      Orders: { customDocumentTitle: titles.ordersCustomDocumentTitle },
      Favorites: {
        title: titles.favoritesTitle,
        customDocumentTitle: titles.favoritesCustomDocumentTitle,
      },
      DietaryRestrictions: {
        title: titles.dietaryRestrictionsTitle,
        customDocumentTitle: titles.dietaryRestrictionsCustomDocumentTitle,
      },
      Notifications: {
        title: titles.notificationsTitle,
        customDocumentTitle: titles.notificationPreferencesCustomDocumentTitle,
      },
    } satisfies ScreenOptionsMap;
  }, [
    stackOptions,
    titles.accountMenuCustomDocumentTitle,
    titles.profileTitle,
    titles.profileCustomDocumentTitle,
    titles.paymentsTitle,
    titles.paymentsCustomDocumentTitle,
    titles.creditAndPromoCodesTitle,
    titles.creditAndPromoCodesCustomDocumentTitle,
    titles.giftCardsTitle,
    titles.accountGiftCardsCustomDocumentTitle,
    titles.giftCardConfirmationTitle,
    titles.giftCardConfirmationCustomDocumentTitle,
    titles.giftCardsTabTitle,
    titles.giftCardsTabCustomDocumentTitle,
    titles.giftCardCheckoutTitle,
    titles.giftCardCheckoutCustomDocumentTitle,
    titles.redeemGiftCardTitle,
    titles.giftCardRedemptionCustomDocumentTitle,
    titles.addressesTitle,
    titles.addressesCustomDocumentTitle,
    titles.ordersCustomDocumentTitle,
    titles.favoritesTitle,
    titles.favoritesCustomDocumentTitle,
    titles.dietaryRestrictionsTitle,
    titles.dietaryRestrictionsCustomDocumentTitle,
    titles.notificationsTitle,
    titles.notificationPreferencesCustomDocumentTitle,
    currentBreakpoint.isXS,
  ]);
};
